import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import { notifySuccess } from '../../services/notify';
import type { ProjectDetail } from '../../types/project';

export function useProjectTimezones() {
  return useQuery<string[]>({
    queryKey: ['projectTimezones'],
    queryFn: () => api.get<string[]>('/project/timezones'),
    staleTime: 24 * 60 * 60 * 1000,
  });
}

export function useProject(id: string) {
  return useQuery<ProjectDetail>({
    queryKey: ['project', id],
    queryFn: () => api.get<ProjectDetail>(`/project/${id}`),
    enabled: !!id,
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (project: Partial<ProjectDetail> & { id: string }) =>
      api.put<ProjectDetail>(`/project/${project.id}`, project),
    onSuccess: (updated) => {
      queryClient.setQueryData<ProjectDetail>(['project', updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useHibernateProject(projectId: string) {
  return useToastMutation(
    ['project', projectId],
    ({ token }: { token: string; projectName: string }) => api.put(`/project/${projectId}/hibernate`, { token }),
    { success: (_d, v) => ({ title: 'Project hibernated successfully', description: v.projectName, timeout: 5000 }) },
  );
}

export function useReactivateProject(projectId: string) {
  return useToastMutation(
    ['project', projectId],
    ({ token }: { token: string; projectName: string }) => api.put(`/project/${projectId}/reactivate`, { token }),
    { success: (_d, v) => ({ title: 'Project re-activated successfully', description: v.projectName, timeout: 5000 }) },
  );
}

export function useDeleteProject(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ token }: { token: string; projectName: string }) => api.delete(`/project/${projectId}`, { token }),
    onSuccess: (_data, vars) => {
      notifySuccess({ title: 'Project deleted successfully', description: vars.projectName, timeout: 5000 });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.removeQueries({ queryKey: ['project', projectId] });
    },
  });
}
